import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { formatDate, formatValue } from './FieldComponents';
import { AlertTriangle, ChevronDown, Download, ListFilter, Loader2, Minus, Plus, RotateCcw, Search, Upload } from 'lucide-react';
import Modal from './Modal';
import { CreateEntry } from './CreateEntry';
import CreateFlatFee from './CreateFlatFee';
import Alert from './Alert';
import BillingTimeImportModal from './BillingTimeImportModal';
import BillingEntriesExportModal from './BillingEntriesExportModal';
import { downloadBillingTimeTemplate } from './billingTimeImport';
import {
    buildBillingEntryEditData,
    buildBillingEntryUpdatePayload,
    readBillingEntryMutationResponse,
} from './billingEntryMutation';
import {
    billingEntryMatchesListFilters,
    createBillingEntryListFilters,
    getBillingEntryAttention,
    getBillingEntryAuthoritativeTotal,
    getBillingEntryEffectiveUserId,
    getBillingEntryEffectiveUserName,
    getBillingEntryKind,
    getBillingEntryQuantity,
    sortBillingEntriesForList,
} from './billingEntryAttention';

export { buildBillingEntryEditData, buildBillingEntryUpdatePayload } from './billingEntryMutation';

const emptyEditEntryData = (caseId) => ({
    id: null,
    billing_rate_id: null,
    date: '',
    rate: null,
    hours: 0,
    final_quantity: null,
    final_total: null,
    description: '',
    user_id: null,
    case_id: caseId,
});

const defaultEntryData = (caseId, kind = 'time') => {
    const isTimeEntry = kind === 'time';
    const quantity = isTimeEntry ? 0 : 1;

    return {
        billing_rate_id: null,
        date: new Date().toISOString().split('T')[0],
        rate: null,
        hours: quantity,
        raw_quantity: quantity,
        quantity,
        description: '',
        user_id: null,
        case_id: caseId,
        draft: false,
        billable: isTimeEntry,
        chargeable: isTimeEntry,
        item_type: kind,
        entry_kind: kind,
        flat_fee_code: '',
        flat_fee_code_2: '',
        unit: '',
    };
};

export const BillingEntries = ({
    case_id,
    rateEntries = [],
    guidedBillingDraft = null,
    onGuidedBillingDraftConsumed = () => {},
    onEntriesChanged = () => {},
}) => {
    const [timeEntries, setTimeEntries] = useState([]);
    const [createEntry, setCreateEntry] = useState(null);
    const [createKind, setCreateKind] = useState('time'); // 'time' or 'flat_fee'
    const [editEntry, setEditEntry] = useState(null);
    const [editEntryOriginal, setEditEntryOriginal] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const updateInFlightRef = useRef(false);
    const [isCreating, setIsCreating] = useState(false);
    const createInFlightRef = useRef(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const deleteInFlightRef = useRef(false);
    const [users, setUsers] = useState([]);
    const [flatFeeOptions, setFlatFeeOptions] = useState([]);
    const [flatFeeOptions2, setFlatFeeOptions2] = useState([]);
    const billingCodesLoadedRef = useRef(false);
    const billingCodesRequestRef = useRef(null);
    const [message, setMessage] = useState(null);
    const [entryData, setEntryData] = useState(() => defaultEntryData(case_id));
    const [editEntryData, setEditEntryData] = useState(() => emptyEditEntryData(case_id));
    const [expandedGroupType, setExpandedGroupType] = useState('time');
    const [importTime, setImportTime] = useState(false);
    const [exportEntries, setExportEntries] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState(createBillingEntryListFilters);
    const [attentionUpdatingIds, setAttentionUpdatingIds] = useState([]);
    const attentionInFlightRef = useRef(new Set());

    const getDefaultEntryData = useCallback((kind = 'time') => defaultEntryData(case_id, kind), [case_id]);

    const closeCreateEntryModal = () => {
        if (isCreating) return;
        setCreateEntry(null);
        setEntryData(getDefaultEntryData(createKind));
    };

    const notifyEntriesChanged = (details = {}) => {
        try {
            const notification = onEntriesChanged(details);
            if (notification && typeof notification.catch === 'function') {
                notification.catch(error => console.error('Failed to refresh billing overview:', error));
            }
        } catch (error) {
            console.error('Failed to refresh billing overview:', error);
        }
    };

    const fetchUsers = useCallback(async () => {
        try {
            const response = await fetch('https://api.casedb.co/user.php?billing_rate=1', {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setUsers(data.users);
            }
        } catch (err) {
            console.error('Failed to fetch users:', err);
        }
    }, []);

    const fetchBillingCodes = useCallback(async () => {
        if (billingCodesLoadedRef.current) return true;
        if (billingCodesRequestRef.current) return billingCodesRequestRef.current;

        billingCodesRequestRef.current = (async () => {
            try {
                const response = await fetch('https://api.casedb.co/billing_codes.php', {
                    credentials: 'include',
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.success) throw new Error(data.message || 'Billing codes could not be loaded.');
                setFlatFeeOptions(Array.isArray(data.flat_fee_code) ? data.flat_fee_code : []);
                setFlatFeeOptions2(Array.isArray(data.flat_fee_code_2) ? data.flat_fee_code_2 : []);
                billingCodesLoadedRef.current = true;
                return true;
            } catch (error) {
                console.error('Failed to fetch billing codes:', error);
                return false;
            } finally {
                billingCodesRequestRef.current = null;
            }
        })();

        return billingCodesRequestRef.current;
    }, []);

    const fetchTimeEntries = useCallback(async () => {
        try {
            const response = await fetch(`https://api.casedb.co/billing_entries.php?case_id=${case_id}`, {
                credentials: 'include'
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) throw new Error(data.message || 'Billing entries could not be loaded.');
            setTimeEntries(Array.isArray(data.billing_entries) ? data.billing_entries : []);
            return true;
        } catch (err) {
            console.error('Failed to fetch time entries:', err);
            return false;
        }
    }, [case_id]);

    const createBillingEntry = async () => {
        if (createInFlightRef.current) return;

        const dataToSend = { ...entryData };
        const quantity = Number(dataToSend.hours ?? dataToSend.quantity ?? dataToSend.raw_quantity ?? 0);
        const normalizedQuantity = Number.isFinite(quantity) ? quantity : 0;

        dataToSend.item_type = createKind;
        dataToSend.entry_kind = createKind;
        dataToSend.hours = normalizedQuantity;
        dataToSend.quantity = normalizedQuantity;
        dataToSend.raw_quantity = normalizedQuantity;
        if (createKind === 'expense') dataToSend.draft = true;

        createInFlightRef.current = true;
        setIsCreating(true);
        setMessage(null);
        try {
            const response = await fetch(`https://api.casedb.co/billing_entries.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(dataToSend)
            });
            const data = await readBillingEntryMutationResponse(response, 'The billing entry could not be created');
            await fetchTimeEntries();
            notifyEntriesChanged({ operation: 'create' });
            setEntryData(getDefaultEntryData(createKind));
            setCreateEntry(null);
            setMessage({ message: data.message || 'Billing entry created successfully.', type: 'success' });
        } catch (err) {
            console.error('Failed to create billing entry:', err);
            setMessage({ message: err.message || 'Failed to create billing entry. Please try again.', type: 'error' });
        } finally {
            createInFlightRef.current = false;
            setIsCreating(false);
        }
    };

    const updateTimeEntry = async () => {
        if (updateInFlightRef.current) return;

        updateInFlightRef.current = true;
        setIsUpdating(true);
        setMessage(null);
        try {
            const response = await fetch(`https://api.casedb.co/billing_entries.php`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(buildBillingEntryUpdatePayload(editEntryData, editEntryOriginal))
            });
            const data = await readBillingEntryMutationResponse(response, 'The billing entry could not be updated');
            const noChanges = data.no_changes === true || data.changed === false || Number(data.affected_rows) === 0;

            if (!noChanges) await fetchTimeEntries();
            if (!noChanges) {
                notifyEntriesChanged({
                    operation: 'update',
                    linkedInvoice: data.linked_invoice || null,
                });
            }
            setEditEntry(null);
            setEditEntryOriginal(null);
            setEditEntryData(emptyEditEntryData(case_id));
            setMessage({
                message: noChanges ? (data.message || 'No billing entry changes were needed.') : (data.message || 'Billing entry updated successfully.'),
                type: noChanges ? 'info' : 'success'
            });
        } catch (err) {
            console.error('Failed to update time entry:', err);
            setMessage({ message: err.message || 'Failed to update billing entry. Please try again.', type: 'error' });
        } finally {
            updateInFlightRef.current = false;
            setIsUpdating(false);
        }
    };

    const deleteTimeEntry = async () => {
        if (deleteInFlightRef.current) return;
        if (!window.confirm('Are you sure you want to delete this billing entry? This action cannot be undone.')) {
            return;
        }

        deleteInFlightRef.current = true;
        setIsDeleting(true);
        setMessage(null);
        try {
            const response = await fetch(`https://api.casedb.co/billing_entries.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ id: editEntryData.id })
            });
            const data = await readBillingEntryMutationResponse(response, 'The billing entry could not be deleted');
            await fetchTimeEntries();
            notifyEntriesChanged({ operation: 'delete' });
            setEditEntry(null);
            setEditEntryOriginal(null);
            setEditEntryData(emptyEditEntryData(case_id));
            setMessage({ message: data.message || 'Billing entry deleted successfully.', type: 'success' });
        } catch (err) {
            console.error('Failed to delete time entry:', err);
            setMessage({ message: err.message || 'Failed to delete billing entry. Please try again.', type: 'error' });
        } finally {
            deleteInFlightRef.current = false;
            setIsDeleting(false);
        }
    };

    const handleEditEntry = (entry) => {
        setEditEntryData(buildBillingEntryEditData(entry, case_id));
        setEditEntryOriginal(entry);
        setEditEntry(true);
    };

    const setAttentionIgnored = async (event, entry, ignored) => {
        event.stopPropagation();
        const entryId = Number(entry?.id);
        const attention = getBillingEntryAttention(entry, rateEntries);
        if (!Number.isInteger(entryId) || entryId <= 0 || !attention.issue_fingerprint) {
            setMessage({
                message: 'Refresh the billing entries before changing this warning.',
                type: 'info',
            });
            return;
        }
        if (attentionInFlightRef.current.has(entryId)) return;

        attentionInFlightRef.current.add(entryId);
        setAttentionUpdatingIds(current => [...current, entryId]);
        setMessage(null);
        try {
            const response = await fetch('https://api.casedb.co/billing_entries.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'set_attention_ignored',
                    id: entryId,
                    case_id: Number(case_id),
                    ignored,
                    expected_issue_fingerprint: attention.issue_fingerprint,
                }),
            });
            const data = await readBillingEntryMutationResponse(response, 'The billing entry warning could not be updated');
            if (data.attention && typeof data.attention === 'object') {
                setTimeEntries(current => current.map(candidate => (
                    Number(candidate?.id) === entryId
                        ? { ...candidate, attention: data.attention }
                        : candidate
                )));
            }
            const refreshed = await fetchTimeEntries();
            setMessage({
                message: refreshed
                    ? (data.message || (ignored ? 'Billing entry warning ignored.' : 'Billing entry warning restored.'))
                    : 'The warning was saved, but the billing entries could not be refreshed. Refresh the page to see the latest list.',
                type: refreshed ? 'success' : 'info',
            });
        } catch (error) {
            console.error('Failed to update billing entry warning:', error);
            setMessage({
                message: error.message || 'The billing entry warning could not be updated.',
                type: 'error',
            });
        } finally {
            attentionInFlightRef.current.delete(entryId);
            setAttentionUpdatingIds(current => current.filter(id => id !== entryId));
        }
    };

    const filteredEntries = useMemo(() => {
        const matches = timeEntries.filter(entry => billingEntryMatchesListFilters(entry, {
            query: searchQuery,
            filters,
            users,
            rateEntries,
        }));
        return sortBillingEntriesForList(matches, rateEntries);
    }, [filters, rateEntries, searchQuery, timeEntries, users]);

    const groupedByType = useMemo(() => Object.values(
        filteredEntries.reduce((acc, entry) => {
            const type = getBillingEntryKind(entry);
            if (!acc[type]) {
                acc[type] = {
                    type,
                    label: type.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()),
                    total_price: 0,
                    total_quantity: 0,
                    total_entries: 0,
                    unresolved_attention: 0,
                    entries: [],
                };
            }

            const quantity = getBillingEntryQuantity(entry);
            const total = getBillingEntryAuthoritativeTotal(entry);
            const attention = getBillingEntryAttention(entry, rateEntries);
            acc[type].total_price += total ?? 0;
            acc[type].total_quantity += quantity ?? 0;
            acc[type].total_entries += 1;
            if (attention.needs_attention && !attention.ignored) acc[type].unresolved_attention += 1;
            acc[type].entries.push(entry);
            return acc;
        }, {})
    ), [filteredEntries, rateEntries]);

    const availableTypes = useMemo(() => Array.from(new Set(
        timeEntries.map(getBillingEntryKind)
    )).sort(), [timeEntries]);

    const teamMemberOptions = useMemo(() => {
        const byId = new Map();
        timeEntries.forEach(entry => {
            const userId = getBillingEntryEffectiveUserId(entry, rateEntries);
            const name = getBillingEntryEffectiveUserName(entry, users, rateEntries);
            if (userId && name) byId.set(userId, name);
        });
        users.forEach(user => {
            const userId = Number(user?.id);
            const name = String(user?.name || user?.full_name || '').trim();
            if (Number.isInteger(userId) && userId > 0 && name && !byId.has(userId)) byId.set(userId, name);
        });
        return Array.from(byId, ([id, name]) => ({ id, name }))
            .sort((left, right) => left.name.localeCompare(right.name));
    }, [rateEntries, timeEntries, users]);

    const activeFilterCount = Object.values(filters).filter(value => value !== '').length;
    const groupTypeSignature = groupedByType.map(group => group.type).join('|');

    const updateFilter = (name, value) => setFilters(current => ({ ...current, [name]: value }));
    const clearFilters = () => setFilters(createBillingEntryListFilters());
    const editEntryKind = getBillingEntryKind(editEntryData);
    const editEntryKindLabel = editEntryKind === 'flat_fee'
        ? 'Flat Fee'
        : editEntryKind === 'expense'
            ? 'Expense'
            : editEntryKind === 'time'
                ? 'Time Entry'
                : 'Billing Entry';

    const updateEditEntryKind = (kind) => {
        setEditEntryData(current => ({
            ...current,
            item_type: kind,
            entry_kind: kind,
        }));
    };

    const updateEditOverride = (field, value) => {
        setEditEntryData(current => ({
            ...current,
            [field]: value === '' ? null : value,
        }));
    };

    useEffect(() => {
        fetchUsers();
        fetchTimeEntries();
    }, [fetchUsers, fetchTimeEntries]);

    useEffect(() => {
        const needsBillingCodes = (createEntry === 1 && createKind === 'flat_fee')
            || (editEntry && editEntryKind === 'flat_fee');
        if (needsBillingCodes) fetchBillingCodes();
    }, [createEntry, createKind, editEntry, editEntryKind, fetchBillingCodes]);

    useEffect(() => {
        const visibleTypes = groupTypeSignature ? groupTypeSignature.split('|') : [];
        if (visibleTypes.length === 0) return;
        setExpandedGroupType(current => {
            if (current === null || visibleTypes.includes(current)) return current;
            return visibleTypes.includes('time') ? 'time' : visibleTypes[0];
        });
    }, [groupTypeSignature]);

    useEffect(() => {
        if (!guidedBillingDraft?.id) return;
        setCreateKind('expense');
        setEntryData({
            ...getDefaultEntryData('expense'),
            ...guidedBillingDraft,
            case_id,
            item_type: 'expense',
            entry_kind: 'expense',
            draft: true,
            hours: guidedBillingDraft.hours ?? guidedBillingDraft.quantity ?? guidedBillingDraft.raw_quantity ?? 1,
            quantity: guidedBillingDraft.quantity ?? guidedBillingDraft.hours ?? guidedBillingDraft.raw_quantity ?? 1,
            raw_quantity: guidedBillingDraft.raw_quantity ?? guidedBillingDraft.quantity ?? guidedBillingDraft.hours ?? 1,
        });
        setCreateEntry(1);
        onGuidedBillingDraftConsumed?.();
    }, [guidedBillingDraft, case_id, getDefaultEntryData, onGuidedBillingDraftConsumed]);

    return (
        <div className='billing-entries'>
            <div className='billing-entry-toolbar'>
                <div className='billing-entry-actions'>
                    <button className='action alt' onClick={() => { setCreateKind('time'); setEntryData(getDefaultEntryData('time')); setCreateEntry(1); }}>+ Time Entry</button>
                    <button className='action alt' onClick={() => { setCreateKind('flat_fee'); setEntryData(getDefaultEntryData('flat_fee')); setCreateEntry(1); }}>+ Flat Fee</button>
                    <button className='action alt' onClick={() => setImportTime(true)}><Upload size={16} /> Import Time</button>
                    <button className='action alt' onClick={() => setExportEntries(true)}><Download size={16} /> Export Entries</button>
                </div>
                <div className='billing-entry-list-controls'>
                    <label className='billing-entry-search'>
                        <Search size={15} aria-hidden='true' />
                        {/*<span className='sr-only'>Search billing entries</span>*/}
                        <input
                            type='search'
                            value={searchQuery}
                            onChange={event => setSearchQuery(event.target.value)}
                            placeholder='Search entries…'
                            aria-label='Search billing entries'
                        />
                    </label>
                    <button
                        type='button'
                        className={`action alt billing-entry-filter-toggle${showFilters || activeFilterCount > 0 ? ' active' : ''}`}
                        onClick={() => setShowFilters(current => !current)}
                        aria-expanded={showFilters}
                        aria-controls='billing-entry-filters'
                    >
                        <ListFilter size={15} />
                        Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                    </button>
                </div>
            </div>
            {showFilters && (
                <div className='billing-entry-filters' id='billing-entry-filters'>
                    <label>
                        <span>Entry type</span>
                        <select value={filters.type} onChange={event => updateFilter('type', event.target.value)}>
                            <option value=''>All types</option>
                            {availableTypes.map(type => (
                                <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        <span>Team member</span>
                        <select value={filters.teamMember} onChange={event => updateFilter('teamMember', event.target.value)}>
                            <option value=''>All team members</option>
                            <option value='unassigned'>Unassigned</option>
                            {teamMemberOptions.map(user => (
                                <option key={user.id} value={user.id}>{user.name}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        <span>Invoice status</span>
                        <select value={filters.invoiceState} onChange={event => updateFilter('invoiceState', event.target.value)}>
                            <option value=''>All entries</option>
                            <option value='unbilled'>Unbilled</option>
                            <option value='invoiced'>Invoiced</option>
                        </select>
                    </label>
                    <label>
                        <span>Attention</span>
                        <select value={filters.attention} onChange={event => updateFilter('attention', event.target.value)}>
                            <option value=''>All entries</option>
                            <option value='needs_attention'>Needs attention</option>
                            <option value='ignored'>Ignored warnings</option>
                            <option value='complete'>Complete</option>
                        </select>
                    </label>
                    <label>
                        <span>From</span>
                        <input type='date' value={filters.dateFrom} onChange={event => updateFilter('dateFrom', event.target.value)} />
                    </label>
                    <label>
                        <span>To</span>
                        <input type='date' value={filters.dateTo} onChange={event => updateFilter('dateTo', event.target.value)} />
                    </label>
                    <button
                        type='button'
                        className='action alt billing-entry-filter-reset'
                        onClick={clearFilters}
                        disabled={activeFilterCount === 0}
                    >
                        <RotateCcw size={14} /> Reset
                    </button>
                </div>
            )}
            <div className='billing-entry-result-count' aria-live='polite'>
                {filteredEntries.length === timeEntries.length
                    ? `${timeEntries.length} ${timeEntries.length === 1 ? 'entry' : 'entries'}`
                    : `${filteredEntries.length} of ${timeEntries.length} entries`}
            </div>
            <details className='instructions billing-time-import-instructions'>
                <summary><ChevronDown size={16} /> Import spreadsheet instructions</summary>
                <div className='billing-time-import-instructions-content'>
                    <span>Upload a CSV, XLS, or XLSX file with Date, Description, and Time columns. An optional User column may contain an exact CaseDB name, username, email, or user ID. Import up to 2,000 rows at a time.</span>
                    <span>Rows with blank Time cells are ignored. Blank User cells use the person importing the file. Time is rounded up using this case&apos;s minimum billing increment.</span>
                    <span>CaseDB previews every entry and resolves the team member and billing rate before anything is saved.</span>
                    <button className='action alt' type='button' onClick={downloadBillingTimeTemplate}><Download size={16} /> Download Template</button>
                </div>
            </details>
            {importTime && (
                <BillingTimeImportModal
                    caseId={case_id}
                    onClose={() => setImportTime(false)}
                    onImported={async (data) => {
                        await fetchTimeEntries();
                        setImportTime(false);
                        setMessage({
                            message: data.already_imported
                                ? 'This spreadsheet import was already completed. No duplicate entries were added.'
                                : data.message || `${data.imported_count || 0} time entries imported successfully.`,
                            type: data.already_imported ? 'info' : 'success',
                        });
                    }}
                />
            )}
            {exportEntries && (
                <BillingEntriesExportModal
                    caseId={case_id}
                    entries={timeEntries}
                    users={users}
                    rateEntries={rateEntries}
                    onClose={() => setExportEntries(false)}
                    onExported={({ filename }) => {
                        setExportEntries(false);
                        setMessage({ message: `${filename} downloaded successfully.`, type: 'success' });
                    }}
                />
            )}
            {createEntry === 1 && (
                <Modal
                    onClose={closeCreateEntryModal}
                    title={createKind === 'time' ? "Add Time Entry" : createKind === 'expense' ? "Draft Expense Entry" : "Add Flat Fee"}
                    single
                    footer={(
                        <div className='modal-footer-actions'>
                            <button
                                className='action alt'
                                disabled={isCreating}
                                onClick={closeCreateEntryModal}
                            >
                                Cancel
                            </button>
                            <button
                                className='action'
                                disabled={isCreating}
                                aria-busy={isCreating}
                                onClick={createBillingEntry}
                            >
                                {isCreating ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    )}
                >
                    {createKind === 'time' ? (
                        <CreateEntry users={users} entryData={entryData} setEntryData={setEntryData} rateEntries={rateEntries} />
                    ) : (
                        <CreateFlatFee
                            users={users}
                            entryData={entryData}
                            setEntryData={setEntryData}
                            flatFeeOptions={flatFeeOptions}
                            flatFeeOptions2={flatFeeOptions2}
                            entryKind={createKind}
                        />
                    )}
                </Modal>
            )}
            {editEntry && (
                <Modal
                    onClose={() => {
                        if (isUpdating || isDeleting) return;
                        setEditEntry(null);
                        setEditEntryOriginal(null);
                        setEditEntryData(emptyEditEntryData(case_id));
                    }}
                    title={`Edit ${editEntryKindLabel}`}
                    single
                    footer={(
                        <div className='modal-footer-actions'>
                            <button
                                className='action destroy'
                                disabled={isUpdating || isDeleting}
                                aria-busy={isDeleting}
                                onClick={() => {
                                    deleteTimeEntry();
                                }}
                            >
                                {isDeleting ? 'Deleting…' : 'Delete'}
                            </button>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                                <button
                                    className='action alt'
                                    disabled={isUpdating || isDeleting}
                                    onClick={() => {
                                        setEditEntry(null);
                                        setEditEntryOriginal(null);
                                        setEditEntryData(emptyEditEntryData(case_id));
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className='action'
                                    disabled={isUpdating || isDeleting}
                                    aria-busy={isUpdating}
                                    onClick={() => {
                                        updateTimeEntry();
                                    }}
                                >
                                    {isUpdating ? 'Saving…' : 'Update'}
                                </button>
                            </div>
                        </div>
                    )}
                >
                    <div className='modal-content-wrapper'>
                        <div className='billing-entry-edit-classification'>
                        <label className='form-group nm'>
                            <span className='subtext'>Entry Type</span>
                            <select
                                className='text-input'
                                value={editEntryKind}
                                onChange={event => updateEditEntryKind(event.target.value)}
                            >
                                <option value='time'>Time</option>
                                <option value='expense'>Expense</option>
                                <option value='flat_fee'>Flat Fee</option>
                            </select>
                        </label>
                        </div>
                        {editEntryKind === 'time' ? (
                            <CreateEntry
                                users={users}
                                entryData={editEntryData}
                                setEntryData={setEditEntryData}
                                rateEntries={rateEntries}
                                embedded
                            />
                        ) : (
                            <CreateFlatFee
                                users={users}
                                entryData={editEntryData}
                                setEntryData={setEditEntryData}
                                flatFeeOptions={flatFeeOptions}
                                flatFeeOptions2={flatFeeOptions2}
                                entryKind={editEntryKind}
                                showTeamMember
                                embedded
                            />
                        )}
                        <fieldset className='billing-entry-overrides'>
                        <legend>Invoice value overrides</legend>
                        <p className='subtext'>Leave either field blank to calculate it from the entry values above.</p>
                        <div className='billing-entry-override-fields'>
                            <label className='form-group nm'>
                                <span className='subtext'>Quantity Override</span>
                                <div className='billing-entry-override-input'>
                                    <input
                                        type='number'
                                        inputMode='decimal'
                                        step='0.01'
                                        value={editEntryData.final_quantity ?? ''}
                                        onChange={event => updateEditOverride('final_quantity', event.target.value)}
                                    />
                                    {editEntryData.final_quantity !== null
                                        && editEntryData.final_quantity !== undefined
                                        && editEntryData.final_quantity !== '' && (
                                            <button
                                                type='button'
                                                className='action alt micro'
                                                onClick={() => updateEditOverride('final_quantity', '')}
                                            >
                                                Use Entry Quantity
                                            </button>
                                        )}
                                </div>
                            </label>
                            <label className='form-group nm'>
                                <span className='subtext'>Total Override</span>
                                <div className='billing-entry-override-input'>
                                    <input
                                        type='number'
                                        inputMode='decimal'
                                        step='0.01'
                                        value={editEntryData.final_total ?? ''}
                                        onChange={event => updateEditOverride('final_total', event.target.value)}
                                    />
                                    {editEntryData.final_total !== null
                                        && editEntryData.final_total !== undefined
                                        && editEntryData.final_total !== '' && (
                                            <button
                                                type='button'
                                                className='action alt micro'
                                                onClick={() => updateEditOverride('final_total', '')}
                                            >
                                                Use Calculated Total
                                            </button>
                                        )}
                                </div>
                            </label>
                        </div>
                        </fieldset>
                    </div>
                </Modal>
            )}
            {groupedByType.length === 0 ? (
                <div className='empty-state'>
                    {timeEntries.length === 0
                        ? 'No billing entries yet for this case.'
                        : 'No billing entries match the current search and filters.'}
                </div>
            ) : (
                groupedByType.map(group => {
                    const expanded = expandedGroupType === group.type;
                    const toggleExpanded = () => setExpandedGroupType(current => current === group.type ? null : group.type);
                    return (
                        <div
                            key={group.type}
                            className={`payment-item billing-entry-group ${expanded ? 'expanded' : ''}`}
                        >
                            <div className='payment-summary'>
                                <div
                                    className='payment-details billing-entry-group-header'
                                    onClick={toggleExpanded}
                                    onKeyDown={event => {
                                        if (event.key !== 'Enter' && event.key !== ' ') return;
                                        event.preventDefault();
                                        toggleExpanded();
                                    }}
                                    role='button'
                                    tabIndex={0}
                                    aria-expanded={expanded}
                                >
                                    <div className='entry-type-item'>
                                        {group.type.split('_').join(' ')} Entries
                                    </div>
                                    <div className='payment-total'>
                                        <span className='subtext'>
                                            {group.type === 'time'
                                                ? `Total Hours: ${group.total_quantity.toFixed(2)} hrs`
                                                : group.total_quantity.toFixed(2)}
                                        </span>
                                        <span className='subtext'>
                                            {group.type === 'time'
                                                ? `Total Price: ${formatValue(group.total_price, 'currency')}`
                                                : formatValue(group.total_price, 'currency')}
                                        </span>
                                        <span className='subtext'>
                                            {group.type === 'time'
                                                ? `Total Entries: ${group.total_entries}`
                                                : group.total_entries}
                                        </span>
                                        {group.unresolved_attention > 0 && (
                                            <span className='billing-entry-group-attention'>
                                                <AlertTriangle size={13} /> {group.unresolved_attention} need attention
                                            </span>
                                        )}
                                        <div className='expand-icon' aria-hidden='true'>
                                            {expanded ? <Minus size={14} /> : <Plus size={14} />}
                                        </div>
                                    </div>
                                </div>
                                {expanded && (
                                    <div className='billing-entry-table-scroll'>
                                        <table className='payment-details-table table-full default billing-entry-table'>
                                            <thead>
                                                <tr className='default'>
                                                    <th>Date</th>
                                                    <th>Description</th>
                                                    <th>Team Member</th>
                                                    <th>Rate</th>
                                                    <th>Hours</th>
                                                    <th>Total</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.entries.map(entry => {
                                                    const attention = getBillingEntryAttention(entry, rateEntries);
                                                    const teamMemberName = getBillingEntryEffectiveUserName(entry, users, rateEntries);
                                                    const quantity = getBillingEntryQuantity(entry);
                                                    const total = getBillingEntryAuthoritativeTotal(entry);
                                                    const rate = entry.rate === null || entry.rate === undefined || entry.rate === ''
                                                        ? null
                                                        : Number(entry.rate);
                                                    const issueLabels = attention.issues.map(issue => issue.label).filter(Boolean);
                                                    const issueCodes = new Set(attention.issue_codes);
                                                    const firstIssue = issueLabels[0] || 'Needs attention';
                                                    const updatingAttention = attentionUpdatingIds.includes(Number(entry.id));
                                                    const dateNeedsAttention = issueCodes.has('missing_date') || issueCodes.has('invalid_date');
                                                    const descriptionNeedsAttention = issueCodes.has('missing_description');
                                                    const teamMemberNeedsAttention = issueCodes.has('missing_team_member');
                                                    const rateNeedsAttention = issueCodes.has('missing_rate') || issueCodes.has('invalid_rate');
                                                    const quantityNeedsAttention = issueCodes.has('missing_quantity') || issueCodes.has('invalid_quantity');
                                                    const totalNeedsAttention = issueCodes.has('invalid_total');
                                                    const displayDate = issueCodes.has('missing_date')
                                                        ? 'Missing'
                                                        : (issueCodes.has('invalid_date')
                                                            ? 'Invalid date'
                                                            : (formatDate(entry.date)[3] || 'Missing'));
                                                    return (
                                                        <tr
                                                            key={entry.id}
                                                            data-entry-id={entry.id}
                                                            className={`payments-table-row billing-entry-row${attention.needs_attention && !attention.ignored ? ' needs-attention' : ''}${attention.ignored ? ' attention-ignored' : ''}`}
                                                            onClick={event => { event.stopPropagation(); handleEditEntry(entry); }}
                                                            tabIndex={0}
                                                            onKeyDown={event => {
                                                                if (event.target !== event.currentTarget || event.key !== 'Enter') return;
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                handleEditEntry(entry);
                                                            }}
                                                        >
                                                            <td className={dateNeedsAttention ? 'billing-entry-missing-value' : ''}>
                                                                {displayDate}
                                                            </td>
                                                            <td className={`align-start billing-entry-description${descriptionNeedsAttention ? ' billing-entry-missing-value' : ''}`}>
                                                                {String(entry.description ?? '').trim() || 'Missing description'}
                                                            </td>
                                                            <td className={teamMemberNeedsAttention ? 'billing-entry-missing-value' : ''}>
                                                                {teamMemberName || 'Unassigned'}
                                                            </td>
                                                            <td className={rateNeedsAttention ? 'billing-entry-missing-value' : ''}>
                                                                {Number.isFinite(rate) ? formatValue(rate, 'currency') : 'Missing'}
                                                            </td>
                                                            <td className={quantityNeedsAttention ? 'billing-entry-missing-value' : ''}>
                                                                {quantity === null ? 'Missing' : quantity.toFixed(2)}
                                                            </td>
                                                            <td className={totalNeedsAttention ? 'billing-entry-missing-value' : ''}>
                                                                {total === null ? 'Missing' : formatValue(total, 'currency')}
                                                            </td>
                                                            <td className='billing-entry-status-cell'>
                                                                <span className='tag'>{entry.status || 'Saved'}</span>
                                                                {attention.needs_attention && (
                                                                    <span
                                                                        className={`billing-entry-attention-tag${attention.ignored ? ' ignored' : ''}`}
                                                                        title={issueLabels.join(', ') || firstIssue}
                                                                    >
                                                                        <AlertTriangle size={12} />
                                                                        {attention.ignored ? 'Warning ignored' : firstIssue}
                                                                        {!attention.ignored && issueLabels.length > 1 ? ` +${issueLabels.length - 1}` : ''}
                                                                    </span>
                                                                )}
                                                                {attention.needs_attention && attention.issue_fingerprint && (
                                                                    <button
                                                                        type='button'
                                                                        className='billing-entry-attention-action'
                                                                        disabled={updatingAttention}
                                                                        aria-busy={updatingAttention}
                                                                        aria-label={`${attention.ignored ? 'Restore' : 'Ignore'} warning for entry ${entry.id}`}
                                                                        title={attention.ignored
                                                                            ? 'Returns this entry to the top of the attention list.'
                                                                            : 'Stops prioritizing this entry. It does not change the entry or its invoice eligibility.'}
                                                                        onClick={event => setAttentionIgnored(event, entry, !attention.ignored)}
                                                                    >
                                                                        {updatingAttention && <Loader2 size={12} className='spin' />}
                                                                        {attention.ignored ? 'Restore warning' : 'Ignore warning'}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })
            )}
            {message && (
                <Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />
            )}
        </div>
    );
};
